// Caller directory (docs/design/contracts.md §30): entry types, phone matching as the server does it, list filters.

export const ENTRY_TYPES = [
  { id: 'Homeowner', label: 'Homeowner' },
  { id: 'Tenant', label: 'Tenant' },
  { id: 'BoardMember', label: 'Board member' },
  { id: 'Vendor', label: 'Vendor' },
  { id: 'Staff', label: 'Staff' },
  { id: 'Other', label: 'Other' },
];

export const entryTypeLabel = (type) => (ENTRY_TYPES.find((t) => t.id === type) || { label: type || 'Other' }).label;

export const isInternalType = (type) => type === 'Staff' || type === 'Vendor';

/** Digits only; 11 digits starting with 1 lose the 1; under 7 digits is no number (PhoneNumbers.Normalize). */
export function normalizePhone(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length < 7 ? null : digits;
}

/** One value per line, comma or semicolon; blanks dropped. */
export const splitList = (text) =>
  String(text || '')
    .split(/[\n,;]/)
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * Entry id → the name of another active entry in a different community holding one of its numbers.
 * Such numbers are ambiguous: the server matches no one on them (DirectoryIndex).
 */
export function sharedNumberOwners(entries) {
  const byNumber = new Map();
  (entries || [])
    .filter((e) => e.isActive !== false)
    .forEach((e) =>
      new Set((e.phoneNumbers || []).map(normalizePhone).filter(Boolean)).forEach((n) => {
        if (!byNumber.has(n)) byNumber.set(n, []);
        byNumber.get(n).push(e);
      })
    );
  const shared = new Map();
  byNumber.forEach((holders) => {
    holders.forEach((e) => {
      const other = holders.find((o) => o.id !== e.id && (o.communityId || null) !== (e.communityId || null));
      if (other && !shared.has(e.id)) shared.set(e.id, other.fullName);
    });
  });
  return shared;
}

/** Search checks names, numbers (by digits too), emails, unit or lot and address. */
export function filterEntries(entries, { query = '', communityId = null, type = null } = {}) {
  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  return (entries || []).filter((e) => {
    if (communityId && e.communityId !== communityId) return false;
    if (type && e.entryType !== type) return false;
    if (!q) return true;
    const text = [e.fullName, e.unitOrLot, e.streetAddress, e.externalId, ...(e.emails || []), ...(e.phoneNumbers || [])].filter(Boolean).join(' ').toLowerCase();
    if (text.includes(q)) return true;
    return digits.length >= 3 && (e.phoneNumbers || []).some((p) => String(p).replace(/\D/g, '').includes(digits));
  });
}

export const sortEntries = (entries) => [...(entries || [])].sort((a, b) => String(a.fullName).localeCompare(String(b.fullName)) || String(a.id).localeCompare(String(b.id)));

export function entryForm(entry, defaultCommunityId = '') {
  const e = entry || {};
  return {
    fullName: e.fullName || '',
    entryType: e.entryType || 'Homeowner',
    communityId: e.id ? e.communityId || '' : e.communityId ?? defaultCommunityId,
    phoneNumbers: (e.phoneNumbers || []).join('\n'),
    unitOrLot: e.unitOrLot || '',
    emails: (e.emails || []).join(', '),
    streetAddress: e.streetAddress || '',
    isActive: e.isActive !== false,
  };
}

export function entryBody(form, entry) {
  return {
    ...(entry || {}),
    fullName: form.fullName.trim(),
    entryType: form.entryType,
    communityId: form.communityId || null,
    phoneNumbers: splitList(form.phoneNumbers),
    emails: splitList(form.emails),
    unitOrLot: form.unitOrLot.trim() || null,
    streetAddress: form.streetAddress.trim() || null,
    isActive: form.isActive,
  };
}

/** "Homeowner, Lot 214" for a call's caller directory details (the caption style of the call's caller tile). */
export const callerDirectoryLine = (caller) => (caller ? [entryTypeLabel(caller.entryType), caller.unitOrLot].filter(Boolean).join(', ') : '');

/** Body of PUT calllog/{id}/community; saveNumber only when the caller's number is to be saved. */
export function setCommunityBody({ communityId, saveNumber, mode, entryId, fullName, entryType, unitOrLot }) {
  const body = { communityId: communityId || null };
  if (!saveNumber) return body;
  body.saveNumber = mode === 'existing' ? { entryId } : { fullName: String(fullName || '').trim(), entryType, unitOrLot: String(unitOrLot || '').trim() || null };
  return body;
}

export const INTERNAL_KIND_LABEL = { agent: 'Agent', user: 'Vertolytics user' };
